import { SaveOutlined, PrinterOutlined, MoneyCollectOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Spin, Tooltip } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CountryInput from '@/components/FormItems/CountryInput';
import { BlobStorageContainerType } from '@/constants';
import CreateBtn from '@/components/app/CreateBtn';
import MainContent from '@/layouts/MainContent';
import { Helmet } from 'react-helmet';
import RegionInput from '@/components/FormItems/RegionInput';
import CityInput from '@/components/FormItems/CityInput';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import PrinterConfigurationsModal from './PrinterConfigurationsModal';
import { clearAllStationsCache } from '@/hooks/data/masterDataHook';
import { StationModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import ManageBalanceModal from '@/pages/CompanyPages/Management/Stations/ManageBalanceModal';

const PAGE_TITLE = 'stationsPage_Title';

const columns: IColumnType<StationModel>[] = [
  {
    title: 'stationsPage_StationName',
    dataIndex: 'name',
    width: 200,
    ellipsis: true,
  },
  {
    title: 'stationsPage_StationCode',
    dataIndex: 'stationCode',
    width: 100,
    ellipsis: true,
  },
  {
    title: 'base.PhoneNumber',
    dataIndex: 'phoneNumber',
    width: 100,
    ellipsis: true,
  },
  {
    title: 'base.Email',
    dataIndex: 'email',
    width: 150,
    ellipsis: true,
  },
  {
    title: 'stationsPage_QuartierName',
    dataIndex: 'quartierName',
    ellipsis: true,
    width: 100,
  },
  {
    title: 'base.Address',
    dataIndex: 'address',
    ellipsis: true,
    width: 100,
  },
  {
    title: 'stationsPage_CityRegionCountry',
    dataIndex: 'id',
    render: (v, record) => `${record.cityName}-${record.regionName}-${record.countryName}`,
    ellipsis: true,
    width: 200,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'name', label: 'stationsPage_StationName' },
  { type: 'TEXT', name: 'stationCode', label: 'stationsPage_StationCode' },
];

const GlobalStationPage = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stationPrinterId, setStationPrinterId] = React.useState<string>();
  const [stationBalanceId, setStationBalanceId] = React.useState<string>();

  const clearCache = () => {
    clearAllStationsCache();
  };

  const createEntity = values => {
    setLoading(true);
    apiSdk.StationService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.StationService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onEdit = (id: string) => {
    setLoading(true);
    apiSdk.StationService.getDetail({ id: id })
      .then(res => {
        if (res) {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...res,
            stationAdministratorUserIds: res.stationAdministratorUsers?.map(x => x.userId),
            stationSupervisorUserIds: res.stationSupervisorUsers?.map(x => x.userId),
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);

  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<StationModel>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/stations/get-stations"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            onEdit(record.id || '');
          }}
          allowDelete={true}
          delete_url="/fleets/stations/delete"
          filters={filters}
          searchBtnAlignLeft
          actionColumnWidth={120}
          extraActions={[
            {
              render: record => {
                return (
                  <Tooltip title={t('stationsPage_ManageBalance')}>
                    <Button size="small" onClick={() => setStationBalanceId(record.id)} type="primary" icon={<MoneyCollectOutlined />} />
                  </Tooltip>
                );
              },
            },
            {
              render: record => {
                return (
                  <Tooltip title={t('stationsPage_PrinterConfigurations')}>
                    <Button size="small" onClick={() => setStationPrinterId(record.id)} type="ghost" icon={<PrinterOutlined />} />
                  </Tooltip>
                );
              },
            },
          ]}
          extraBtns={
            <CreateBtn
              onClick={() => {
                setIsNewForm(true);
                setFormVisible(true);
                form.resetFields();
              }}
            />
          }
          onDeleteCompleted={clearCache}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.large}
            className="nta-custom-form"
            title={isNewForm ? t('stationsPage_AddStation') : t('stationsPage_UpdateStation')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Row gutter={6}>
                  <Col md={12}>
                    <Form.Item label={t('stationsPage_StationName')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('stationsPage_StationCode')} name="stationCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label={t('stationsPage_StartingBalance')}
                      name="startingBalance"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>
                    <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <CountryInput
                        onChange={() => {
                          form.setFieldValue('regionId', null);
                        }}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.Region')} name="regionId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <RegionInput
                        onChange={() => {
                          form.setFieldValue('cityId', null);
                        }}
                        countryId={countryId}
                        disabled={!countryId}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.City')} name="cityId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <CityInput regionId={regionId} disabled={!regionId} />
                    </Form.Item>
                    <Form.Item label={t('stationsPage_Latitude')} name="latitude" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber className="w-100" />
                    </Form.Item>
                    <Form.Item label={t('stationsPage_Longitude')} name="longitude" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber className="w-100" />
                    </Form.Item>
                  </Col>
                  <Col md={12}>
                    <Form.Item label={t('stationsPage_Quartier')} name="quartierName">
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('base.PhoneNumber')} name="phoneNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('base.Email')} name="email">
                      <Input type="email" />
                    </Form.Item>

                    <Form.Item label={t('stationsPage_displayPhoneNumbers')} name="displayPhoneNumber">
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('base.Address')} name="address">
                      <Input.TextArea />
                    </Form.Item>
                    <Form.Item label="  " colon={false} name="isConnectionStation" valuePropName="checked">
                      <Checkbox>{t('stationsPage_IsConnectionStation')}</Checkbox>
                    </Form.Item>
                    <Form.Item name="stationImageUrl" label={t('stationsPage_StationImageUrl')}>
                      <SingleImageUpload blobStorageContainer={BlobStorageContainerType.StationsContainer} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {stationPrinterId ? (
        <PrinterConfigurationsModal
          stationId={stationPrinterId}
          onClose={() => {
            setStationPrinterId(undefined);
          }}
        />
      ) : null}
      {stationBalanceId ? (
        <ManageBalanceModal
          stationId={stationBalanceId}
          onClose={(reload?: boolean) => {
            setStationBalanceId(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
    </MainContent>
  );
};
export default GlobalStationPage;
